import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { isNullOrUndefined } from '../../../../domain/utils/typeUtils';
import { primitiveOrArrayExec } from '../../../../domain/utils/arrayUtils';
import { nullValidator } from '../../../propTypes/validators';
import { theme } from '../../../../domain/theme';
import { facePaint } from '../../../../domain/theme/utils/facePaint';
import {
  numberOrArrayPlusNull,
  numberOrStringOrArrayPlusNull,
  stringOrArrayPlusNull
} from '../../../propTypes';

const containsZero = width => primitiveOrArrayExec(width, 'some', w => w === 0);

// If resolvedWidth contains a "0" value, set that element's display to none
const getResolvedDisplay = resolvedWidth => {
  if (containsZero(resolvedWidth)) {
    const display = Array.isArray(resolvedWidth)
      ? [...resolvedWidth]
      : resolvedWidth;

    return primitiveOrArrayExec(display, 'map', w => {
      if (w === 0) {
        return 'none';
      }
      if (w === null) {
        return;
      }
      return 'block';
    });
  }

  return;
};

const resolveOffsetObject = object => {
  const left = object.left ? theme.grid.columns(object.left) : 0;
  const right = object.right ? theme.grid.columns(object.right) : 0;
  return `0 ${right} 0 ${left}`;
};

const columnOffsetParser = value => {
  if (Array.isArray(value)) {
    return value.map(val =>
      isNullOrUndefined(val) ? val : resolveOffsetObject(val)
    );
  }
  return isNullOrUndefined(value) ? value : resolveOffsetObject(value);
};

export const GridColumn = ({
  width = 12,
  offset,
  order,
  sticky = false,
  children,
  childrenMarginBottom,
  className
}) => {
  const resolvedWidth = theme.grid.columns(width);
  const resolvedMaxWidth = resolvedWidth;
  const resolvedOffset = columnOffsetParser(offset);

  const stickyCss = sticky
    ? css`
        position: sticky;
        top: 0;
        align-self: flex-start;
        padding-top: 0 !important;
      `
    : undefined;

  return (
    <div
      className={className}
      css={css`
        ${facePaint({
          width: resolvedWidth,
          'max-width': resolvedMaxWidth,
          margin: resolvedOffset,
          display: getResolvedDisplay(resolvedWidth),
          order
        })};
        flex: 1 0 auto;
        & > * {
          width: auto;
          ${facePaint({
            'margin-bottom': childrenMarginBottom
              ? theme.spacing.parseUnknownData(childrenMarginBottom)
              : undefined
          })};
        }
        & > *:last-child {
          margin-bottom: 0;
        }
        ${stickyCss}
      `}
    >
      {children}
    </div>
  );
};

const offsetShape = PropTypes.shape({
  left: PropTypes.number,
  right: PropTypes.number
});

GridColumn.propTypes = {
  width: numberOrStringOrArrayPlusNull,
  offset: PropTypes.oneOfType([
    offsetShape,
    PropTypes.arrayOf(PropTypes.oneOfType([nullValidator, offsetShape]))
  ]),
  // flex order
  order: numberOrArrayPlusNull,
  sticky: PropTypes.bool,
  children: PropTypes.node.isRequired,
  childrenMarginBottom: stringOrArrayPlusNull,
  className: PropTypes.string
};
